import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { ChipRow } from './ChipRow'
import { CountBadge, FilterChip, TagChip } from './Chip'
import { FloatingMenu } from './FloatingMenu'
import { TextInput } from './TextInput'
import { Button } from './Button'
import { Pressable } from './Pressable'
import { getNlsString } from '../../nls/nlsRuntime'
import './labeledDropdown.css'
import './multiSelect.css'

export type MultiSelectOption<T extends string | number> = {
  value: T
  label: ReactNode
  searchText?: string
  disabled?: boolean
}

export type MultiSelectProps<T extends string | number> = {
  options: MultiSelectOption<T>[]
  selectedValues: T[]
  onChange: (next: T[]) => void
  label: string
  ariaLabel?: string
  placeholder?: string
  emptyLabel?: string
  searchPlaceholder?: string
  searchable?: boolean
  /** Optional callback fired when search query changes. */
  onSearch?: (query: string) => void
  className?: string
  use?: 'filter' | 'field'
  size?: 'sm' | 'md'
  disabled?: boolean
  // filter only
  collapseMode?: 'auto' | 'chips' | 'dropdown'
  forceCollapsed?: boolean
  summaryLabel?: string
  selectedSummaryMode?: 'count' | 'inline-truncated'
  // field only
  onCreateOption?: (text: string) => void
}

function optionSearchText<T extends string | number>(option: MultiSelectOption<T>): string {
  if (option.searchText) return option.searchText.toLowerCase()
  if (typeof option.label === 'string') return option.label.toLowerCase()
  if (typeof option.label === 'number') return String(option.label).toLowerCase()
  return String(option.value).toLowerCase()
}

function pluralizeLabel(label: string, count: number): string {
  if (count === 1) return `${count} ${label}`
  return `${count} ${label.endsWith('s') ? label : `${label}s`}`
}

export const MultiSelect = <T extends string | number>({
  options,
  selectedValues,
  onChange,
  label,
  ariaLabel,
  placeholder = getNlsString('NLSCommonInputSelectPlaceholder'),
  emptyLabel = getNlsString('NLSCommonEmptyNoOptions'),
  searchPlaceholder = getNlsString('NLSCommonInputSearchOptionsPlaceholder'),
  searchable,
  onSearch,
  className,
  use = 'filter',
  size = 'md',
  disabled = false,
  collapseMode = 'auto',
  forceCollapsed = false,
  summaryLabel,
  selectedSummaryMode = 'count',
  onCreateOption,
}: MultiSelectProps<T>) => {
  const hostRef = useRef<HTMLDivElement | null>(null)
  const measureRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLButtonElement | null>(null)
  const fieldAnchorRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [autoCollapsed, setAutoCollapsed] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  const collapsed = forceCollapsed
    ? true
    : collapseMode === 'chips'
      ? false
      : collapseMode === 'dropdown'
        ? true
        : autoCollapsed

  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    if (!open) {
      setQuery('')
      setHighlightedIndex(-1)
    }
  }

  const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues])
  const useSearch = searchable ?? options.length >= 8
  const effectiveAriaLabel = ariaLabel ?? `${label} filter`

  useEffect(() => {
    if (use !== 'filter') return
    if (forceCollapsed || collapseMode === 'chips' || collapseMode === 'dropdown') return
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(() => {
      const host = hostRef.current
      const measure = measureRef.current
      if (!host || !measure) return
      setAutoCollapsed(measure.scrollWidth > host.clientWidth + 1)
    })
    if (hostRef.current) observer.observe(hostRef.current)
    if (measureRef.current) observer.observe(measureRef.current)
    return () => observer.disconnect()
  }, [use, collapseMode, forceCollapsed, label, options, selectedValues])

  const filteredOptions = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter((option) => optionSearchText(option).includes(needle))
  }, [options, query])

  const selectedLabels = useMemo(() => {
    if (selectedValues.length === 0) return []
    const labelsByValue = new Map(
      options.map((option) => {
        const labelText =
          typeof option.label === 'string' || typeof option.label === 'number'
            ? String(option.label)
            : option.searchText ?? String(option.value)
        return [option.value, labelText]
      }),
    )
    return selectedValues.map((value) => labelsByValue.get(value) ?? String(value))
  }, [options, selectedValues])

  const inlineSummary = selectedLabels.join(', ')
  const summary =
    selectedValues.length > 0
      ? pluralizeLabel(summaryLabel ?? label, selectedValues.length)
      : placeholder
  const useInlineSummary = selectedSummaryMode === 'inline-truncated' && inlineSummary.length > 0
  const triggerValue = useInlineSummary ? inlineSummary : summary

  const toggleValue = (value: T) => {
    if (selectedSet.has(value)) {
      onChange(selectedValues.filter((entry) => entry !== value))
      return
    }
    onChange([...selectedValues, value])
  }

  const removeValue = (value: T) => {
    onChange(selectedValues.filter((entry) => entry !== value))
  }

  const handleSelectAll = () => {
    const visibleEnabled = filteredOptions.filter((option) => !option.disabled).map((option) => option.value)
    const next = Array.from(new Set([...selectedValues, ...visibleEnabled]))
    onChange(next)
  }

  const handleClear = () => {
    onChange([])
  }

  // Field create option check
  const trimmedQuery = query.trim()
  const hasExactMatch = useMemo(() => {
    if (!trimmedQuery) return false
    const lower = trimmedQuery.toLowerCase()
    return options.some((opt) => {
      const text =
        typeof opt.label === 'string' || typeof opt.label === 'number'
          ? String(opt.label)
          : opt.searchText ?? String(opt.value)
      return text.toLowerCase() === lower
    })
  }, [options, trimmedQuery])

  const canCreate = Boolean(onCreateOption && trimmedQuery.length > 0 && !hasExactMatch)
  const totalFieldItems = filteredOptions.length + (canCreate ? 1 : 0)

  const handleFieldKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && query === '' && selectedValues.length > 0) {
      removeValue(selectedValues[selectedValues.length - 1])
      return
    }

    if (e.key === 'Escape') {
      setOpen(false)
      return
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (totalFieldItems > 0) {
        setHighlightedIndex((prev) => (prev + 1) % totalFieldItems)
      }
      return
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      if (totalFieldItems > 0) {
        setHighlightedIndex((prev) => (prev - 1 + totalFieldItems) % totalFieldItems)
      }
      return
    }

    if (e.key === 'Enter') {
      e.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }

      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        toggleValue(filteredOptions[highlightedIndex].value)
        return
      }

      if (
        (highlightedIndex === filteredOptions.length ||
          (highlightedIndex === -1 && filteredOptions.length === 0)) &&
        canCreate
      ) {
        onCreateOption!(trimmedQuery)
        setQuery('')
        return
      }

      if (highlightedIndex === -1 && filteredOptions.length === 1) {
        toggleValue(filteredOptions[0].value)
        return
      }

      if (canCreate && filteredOptions.length === 0) {
        onCreateOption!(trimmedQuery)
        setQuery('')
      }
    }
  }

  if (use === 'field') {
    return (
      <div
        ref={fieldAnchorRef}
        className={[
          'dsTags',
          size === 'sm' ? 'dsTags--sm' : 'dsTags--md',
          isFocused || open ? 'is-focus' : '',
          disabled ? 'is-disabled' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => { if (!disabled) inputRef.current?.focus() }}
      >
        {selectedValues.map((val) => {
          const opt = options.find((o) => o.value === val)
          const labelStr = opt
            ? typeof opt.label === 'string' || typeof opt.label === 'number'
              ? String(opt.label)
              : opt.searchText ?? String(val)
            : String(val)
          return (
            <TagChip
              key={String(val)}
              size={size === 'sm' ? 'sm' : 'md'}
              removeLabel={getNlsString('NLSCommonMultiSelectRemoveTag', { label: labelStr })}
              onRemove={() => { if (!disabled) removeValue(val) }}
            >
              {opt?.label ?? val}
            </TagChip>
          )
        })}

        <input
          ref={inputRef}
          type="text"
          value={query}
          placeholder={selectedValues.length === 0 ? placeholder : ''}
          aria-label={effectiveAriaLabel}
          disabled={disabled}
          onChange={(e) => {
            if (disabled) return
            setQuery(e.target.value)
            onSearch?.(e.target.value)
            if (!open) setOpen(true)
          }}
          onFocus={() => {
            if (disabled) return
            setIsFocused(true)
            setOpen(true)
          }}
          onBlur={() => {
            setIsFocused(false)
          }}
          onKeyDown={disabled ? undefined : handleFieldKeyDown}
        />

        <FloatingMenu
          anchorRef={fieldAnchorRef}
          isOpen={!disabled && open && (filteredOptions.length > 0 || canCreate || Boolean(emptyLabel))}
          onClose={() => setOpen(false)}
          placement="bottom-start"
          className="dsDrop is-open"
          role="listbox"
          ariaLabel={effectiveAriaLabel}
          matchAnchorWidth
        >
          <div className="dsDrop__items">
            {filteredOptions.length === 0 && !canCreate ? (
              <span className="dsDrop__empty">{emptyLabel}</span>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = selectedSet.has(option.value)
                const isHighlighted = highlightedIndex === index
                return (
                  <Pressable
                    key={String(option.value)}
                    role="option"
                    aria-selected={isSelected}
                    disabled={option.disabled}
                    className={`dsDrop__item${isSelected ? ' is-active' : ''}${isHighlighted ? ' is-hovered' : ''}`}
                    onClick={() => {
                      if (option.disabled) return
                      toggleValue(option.value)
                      inputRef.current?.focus()
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                  >
                    <label className="dsCb dsCb--sm" aria-hidden="true">
                      <input
                        type="checkbox"
                        className="dsCb__input"
                        checked={isSelected}
                        readOnly
                        tabIndex={-1}
                      />
                      <span className="dsCb__box">
                        <svg viewBox="0 0 24 24">
                          <path d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    </label>
                    <span className="dsDrop__itemLabel">{option.label}</span>
                  </Pressable>
                )
              })
            )}

            {canCreate && (
              <>
                {filteredOptions.length > 0 && <div className="dsDrop__divider" aria-hidden="true" />}
                <Pressable
                  className={`dsDrop__item dsDrop__item--create${highlightedIndex === filteredOptions.length ? ' is-hovered' : ''}`}
                  onClick={() => {
                    onCreateOption!(trimmedQuery)
                    setQuery('')
                    inputRef.current?.focus()
                  }}
                  onMouseEnter={() => setHighlightedIndex(filteredOptions.length)}
                >
                  <span className="dsDrop__itemIcon" aria-hidden="true">+</span>
                  <span className="dsDrop__itemLabel">
                    {getNlsString('NLSCommonMultiSelectCreateOption', { text: trimmedQuery })}
                  </span>
                  <span className="dsDrop__itemMeta" aria-hidden="true">↵</span>
                </Pressable>
              </>
            )}
          </div>
        </FloatingMenu>
      </div>
    )
  }

  // use === 'filter'
  return (
    <div
      ref={hostRef}
      className={[
        'adaptiveMultiFilter',
        collapsed ? 'adaptiveMultiFilter--dropdown' : 'adaptiveMultiFilter--chips',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div ref={measureRef} className="adaptiveMultiFilterMeasure" aria-hidden="true">
        <div className="adaptiveMultiFilterChipSurface adaptiveMultiFilterChipSurface--measure">
          <span className="adaptiveMultiFilterChipLabel">{label}</span>
          <div className="adaptiveMultiFilterMeasureRow">
            {options.map((option) => (
              <span
                key={`measure-${String(option.value)}`}
                className={`adaptiveMultiFilterChip${selectedSet.has(option.value) ? ' isActive' : ''}`}
              >
                {option.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      {collapsed ? (
        <>
          <Pressable
            ref={anchorRef}
            className={[
              'labeledDropdownTrigger',
              size === 'sm' ? 'labeledDropdownTrigger--sm' : '',
              open ? 'isOpen' : '',
              useInlineSummary ? 'adaptiveMultiFilterTrigger--inlineSummary' : '',
              'adaptiveMultiFilterTrigger',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-label={effectiveAriaLabel}
            aria-haspopup="listbox"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
          >
            <span className="labeledDropdownLabel">{useInlineSummary ? `${label}:` : label}</span>
            {!useInlineSummary && <span className="labeledDropdownDivider" aria-hidden="true" />}
            <span
              className={`labeledDropdownValue${useInlineSummary ? ' adaptiveMultiFilterInlineSummary' : ''}`}
              title={useInlineSummary ? inlineSummary : undefined}
              data-hover-native={useInlineSummary ? 'true' : undefined}
            >
              {triggerValue}
            </span>
            {selectedValues.length > 0 && (
              <CountBadge value={selectedValues.length} />
            )}
            <span className="labeledDropdownCaret" aria-hidden="true">
              <ExpandMoreRoundedIcon fontSize="inherit" />
            </span>
          </Pressable>
          <FloatingMenu
            anchorRef={anchorRef}
            isOpen={open}
            onClose={() => setOpen(false)}
            placement="bottom-start"
            className="dsDrop is-open adaptiveMultiFilterMenu"
            role="listbox"
            ariaLabel={effectiveAriaLabel}
          >
            {useSearch ? (
              <div className="adaptiveMultiFilterMenuSearch">
                <TextInput
                  value={query}
                  onChange={(val) => {
                    setQuery(val)
                    onSearch?.(val)
                  }}
                  placeholder={searchPlaceholder}
                  aria-label={`${effectiveAriaLabel} search`}
                  size="sm"
                  variant="toolbar"
                />
              </div>
            ) : null}

            <div className="dsDrop__bulk">
              <span>
                {getNlsString('NLSCommonMultiSelectSelectedCount', {
                  count: selectedValues.length,
                  total: options.length,
                })}
              </span>
              <span>
                <Button variant="link" size="sm" onClick={handleSelectAll}>
                  {getNlsString('NLSCommonMultiSelectSelectAll')}
                </Button>{' '}
                <Button variant="link" size="sm" onClick={handleClear}>
                  {getNlsString('NLSCommonMultiSelectClear')}
                </Button>
              </span>
            </div>

            <div className="dsDrop__items adaptiveMultiFilterMenuItems">
              {filteredOptions.length === 0 ? (
                <span className="dsDrop__empty">{emptyLabel}</span>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedSet.has(option.value)
                  return (
                    <Pressable
                      key={String(option.value)}
                      role="option"
                      aria-selected={isSelected}
                      disabled={option.disabled}
                      className={`dsDrop__item adaptiveMultiFilterOption${isSelected ? ' is-active' : ''}`}
                      onClick={() => {
                        if (option.disabled) return
                        toggleValue(option.value)
                      }}
                    >
                      <label className="dsCb dsCb--sm" aria-hidden="true">
                        <input
                          type="checkbox"
                          className="dsCb__input"
                          checked={isSelected}
                          readOnly
                          tabIndex={-1}
                        />
                        <span className="dsCb__box">
                          <svg viewBox="0 0 24 24">
                            <path d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                      </label>
                      <span className="dsDrop__itemLabel">{option.label}</span>
                    </Pressable>
                  )
                })
              )}
            </div>
          </FloatingMenu>
        </>
      ) : (
        <div className="adaptiveMultiFilterChipSurface" role="group" aria-label={effectiveAriaLabel}>
          <span className="adaptiveMultiFilterChipLabel">{label}</span>
          <ChipRow ariaLabel={effectiveAriaLabel} className="adaptiveMultiFilterChipRow">
            {options.map((option) => {
              const isSelected = selectedSet.has(option.value)
              return (
                <FilterChip
                  key={String(option.value)}
                  selected={isSelected}
                  disabled={option.disabled}
                  onClick={() => toggleValue(option.value)}
                >
                  {option.label}
                </FilterChip>
              )
            })}
          </ChipRow>
        </div>
      )}
    </div>
  )
}
